import { AGTypeError } from '@/errors/AGTypeError';
import { logger, objectHasAll, objectHasSome } from '@/util';
import callBackendApi from '@/apis/call';
import type { DeleteContactsResponse } from './types';

const DESIRED_LIST_UPDATE_FIELDS = ['assignedTo', 'name', 'isComplete'];

const REQUIRED_LIST_UPDATE_MULTIPLE_FIELDS = [
  'assignedToSubs',
  'coordinatorSubs',
  'numAdditionalContacts',
];

const REQUIRED_LIST_UPDATE_CONTACT_FIELDS = ['contactStatus'];

const REQUIRED_LIST_RELEASE_CONTACT_FIELDS = ['releaseContactsState', 'releasedForSub'];

export const updateList = async (
  token: string,
  listId: number,
  values: Record<string, unknown>
) => {
  logger.debug('updateList() called', {});

  const hasAnyRequired = objectHasSome(values, DESIRED_LIST_UPDATE_FIELDS);
  if (!hasAnyRequired) throw new AGTypeError('Missing at least one required field');

  const data = await callBackendApi({
    token,
    method: 'put',
    endpoint: `/lists/${listId}`,
    data: values,
  });
  return data;
};

export const updateAssigneeForList = async (
  token: string,
  listId: number,
  assigneeId: string,
  values: Record<string, unknown>
) => {
  logger.debug('updateAssigneeForList() called', {});

  const hasAnyRequired = objectHasSome(values, ['isComplete']);
  if (!hasAnyRequired) throw new AGTypeError('Missing at least one required field');

  const data = await callBackendApi({
    token,
    method: 'put',
    endpoint: `/lists/${listId}/assignees/${assigneeId}`,
    data: values,
  });
  return data;
};

export const removeAssigneesForList = async (
  token: string,
  listId: number,
  assigneeIds: string[]
) => {
  logger.debug('updateAssigneeForList() called', {});

  const query = encodeURIComponent(`["${assigneeIds.join('","')}"]`);

  const data = await callBackendApi({
    token,
    method: 'delete',
    endpoint: `/lists/${listId}/assignees?ids=${query}`,
  });
  return data;
};

export const deleteList = async (token: string, listId: number) => {
  logger.debug('deleteList() called', {});

  const data = await callBackendApi({ token, method: 'delete', endpoint: `/lists/${listId}` });
  return data;
};

export const updateAssignMultipleList = async (
  token: string,
  listId: number,
  values: Record<string, unknown>
) => {
  logger.debug('updateAssignMultipleList() called', {});

  const hasAllRequired = objectHasAll(values, REQUIRED_LIST_UPDATE_MULTIPLE_FIELDS);
  if (!hasAllRequired) throw new AGTypeError('Missing at least one required field');

  const data = await callBackendApi({
    token,
    method: 'put',
    endpoint: `/lists/assignmultiple/${listId}`,
    data: values,
  });
  return data;
};

export const updateAssignContactStatus = async (
  token: string,
  listId: number,
  values: Record<string, unknown>
) => {
  logger.debug('updateAssignContactStatus() called', {});

  const hasAnyRequired = objectHasSome(values, REQUIRED_LIST_UPDATE_CONTACT_FIELDS);
  if (!hasAnyRequired) throw new AGTypeError('Missing at least one required field');

  const data = await callBackendApi({
    token,
    method: 'put',
    endpoint: `/lists/assigncontactstatus/${listId}`,
    data: values,
  });
  return data;
};

export const updateCallingStatus = async (
  token: string,
  listId: number,
  contactId: number,
  values: { callingStatus: string; callingStatusSetterSub: string; assignedToSub?: string }
) => {
  logger.debug('updateCallingStatus() called', {});

  const data = await callBackendApi({
    token,
    method: 'put',
    endpoint: `/lists/${listId}/contact/${contactId}`,
    data: values,
  });
  return data;
};

export const updateReleaseContacts = async (
  token: string,
  listId: number,
  values: Record<string, unknown>
) => {
  logger.debug('updateAssignContactStatus() called', {});

  const hasAnyRequired = objectHasSome(values, REQUIRED_LIST_RELEASE_CONTACT_FIELDS);
  if (!hasAnyRequired) throw new AGTypeError('Missing at least one required field');

  const data = await callBackendApi({
    token,
    method: 'put',
    endpoint: `/lists/${listId}/releasecontacts`,
    data: values,
  });
  return data;
};

export const deleteContactsFromList = async (
  token: string,
  listId: number,
  contactIds: number[]
) => {
  const result = await callBackendApi({
    endpoint: `lists/${listId}/contacts?contactIds=${encodeURIComponent(
      JSON.stringify(contactIds)
    )}`,
    method: 'delete',
    token,
  });
  return result as DeleteContactsResponse;
};
